'use client';

import { Box, Button, Field, Flex, Heading, Input, Text, VStack } from '@chakra-ui/react';
import { ArrowRight } from 'lucide-react';
import { useEffect, useRef, useState, type ReactNode } from 'react';
import { useAuth } from '@/app/auth-provider';
import { IntegrationsSettingsPanel } from '@/components/settings/IntegrationsSettingsPanel';
import type { Project } from '@/contexts/ProjectContext';
import {
  clearSetupDraft,
  normalizeSetupWebsite,
  readSetupDraft,
  sessionDraftStorage,
  setupDraftKey,
  validateWorkspaceIdentity,
  writeIdentityDraft,
  type IdentitySetupDraft,
} from '@/lib/onboarding/setup-draft';
import { isGoogleWorkspaceExportDeliveryEnabledClient } from '@/lib/research-assistant/export-feature-flags';

export interface WorkspaceSetupWizardProps {
  project: Project | null;
  workspaceId?: string | null;
  workspaceName?: string | null;
  onCreate: (input: {
    name: string;
    website_url?: string;
    idempotency_key: string;
  }) => Promise<Project>;
  onOpen: (status: 'completed' | 'skipped') => Promise<void>;
  activeModuleId?: string | null;
  onActiveModuleChange?: (id: string | null) => void;
  returnTo?: string;
}

export interface WorkspaceSetupWizardViewProps extends WorkspaceSetupWizardProps {
  identityDraftKey?: string;
  renderModules: (project: Project, onConfiguredCountChange: (count: number) => void) => ReactNode;
}

/** The two-screen flow, independent of auth and project providers. */
export function WorkspaceSetupWizardView({
  project,
  workspaceName,
  onCreate,
  onOpen,
  identityDraftKey,
  renderModules,
}: WorkspaceSetupWizardViewProps) {
  const [identity, setIdentity] = useState<IdentitySetupDraft>({
    name: '',
    website: '',
    attemptKey: '',
  });
  const [hydrated, setHydrated] = useState(false);
  const [createdProject, setCreatedProject] = useState<Project | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [configuredCount, setConfiguredCount] = useState(0);
  const saving = useRef(false);
  const headingRef = useRef<HTMLHeadingElement>(null);
  const currentProject = project || createdProject;

  useEffect(() => {
    const saved = identityDraftKey
      ? readSetupDraft<IdentitySetupDraft>(sessionDraftStorage(), identityDraftKey)
      : null;
    setIdentity(
      saved &&
        typeof saved.name === 'string' &&
        typeof saved.website === 'string' &&
        saved.attemptKey
        ? saved
        : { name: '', website: '', attemptKey: crypto.randomUUID() }
    );
    setHydrated(true);
  }, [identityDraftKey]);

  useEffect(() => {
    if (hydrated && identityDraftKey && !currentProject)
      writeIdentityDraft(sessionDraftStorage(), identityDraftKey, identity);
  }, [currentProject, hydrated, identity, identityDraftKey]);

  useEffect(() => {
    if (currentProject) headingRef.current?.focus();
  }, [currentProject?.id]);

  async function createWorkspace() {
    if (saving.current || !hydrated) return;
    const invalid = validateWorkspaceIdentity(identity.name, identity.website);
    if (invalid) {
      setError(invalid);
      return;
    }
    saving.current = true;
    setBusy(true);
    setError(null);
    try {
      // Flush before navigating or waiting on the network, including on a rapid submit.
      if (identityDraftKey) writeIdentityDraft(sessionDraftStorage(), identityDraftKey, identity);
      const result = await onCreate({
        name: identity.name.trim(),
        ...(identity.website.trim()
          ? { website_url: normalizeSetupWebsite(identity.website) }
          : {}),
        idempotency_key: identity.attemptKey,
      });
      setCreatedProject(result);
      if (identityDraftKey) clearSetupDraft(sessionDraftStorage(), identityDraftKey);
    } catch (reason) {
      if (
        reason instanceof Error &&
        'code' in reason &&
        reason.code === 'SETUP_ATTEMPT_UNAVAILABLE'
      ) {
        const next = { ...identity, attemptKey: crypto.randomUUID() };
        setIdentity(next);
        if (identityDraftKey) writeIdentityDraft(sessionDraftStorage(), identityDraftKey, next);
      }
      setError(
        reason instanceof Error ? reason.message : 'Unable to create your workspace. Try again.'
      );
    } finally {
      saving.current = false;
      setBusy(false);
    }
  }

  async function openWorkspace() {
    if (saving.current) return;
    saving.current = true;
    setBusy(true);
    setError(null);
    try {
      await onOpen(configuredCount ? 'completed' : 'skipped');
    } catch (reason) {
      setError(
        reason instanceof Error ? reason.message : 'Unable to open your workspace. Try again.'
      );
    } finally {
      saving.current = false;
      setBusy(false);
    }
  }

  return (
    <Box
      w="full"
      maxW={currentProject ? '920px' : '460px'}
      mx="auto"
      px={{ base: '4', md: '6' }}
      pt={{ base: '6', md: currentProject ? '8' : '16' }}
      pb="6"
    >
      {!currentProject ? (
        <Box
          as="form"
          onSubmit={event => {
            event.preventDefault();
            void createWorkspace();
          }}
        >
          <Heading
            as="h1"
            fontSize="2xl"
            fontWeight="650"
            letterSpacing="-0.02em"
            color="ui.text.primary"
          >
            Create your workspace
          </Heading>
          <Text mt="2" mb="7" fontSize="sm" color="ui.text.secondary">
            A home for your company’s tools, data and work.
          </Text>
          <VStack align="stretch" gap="5">
            <Field.Root required invalid={Boolean(error && !identity.name.trim())}>
              <Field.Label>
                Workspace name
                <Field.RequiredIndicator />
              </Field.Label>
              <Input
                autoFocus
                autoComplete="organization"
                placeholder="Company name"
                value={identity.name}
                disabled={busy}
                onChange={event => {
                  setIdentity(value => ({ ...value, name: event.target.value }));
                  setError(null);
                }}
              />
            </Field.Root>
            <Field.Root>
              <Field.Label>
                Company website{' '}
                <Text as="span" color="ui.text.tertiary" fontWeight="400">
                  (optional)
                </Text>
              </Field.Label>
              <Input
                autoComplete="url"
                inputMode="url"
                placeholder="example.com"
                value={identity.website}
                disabled={busy}
                onChange={event => {
                  setIdentity(value => ({ ...value, website: event.target.value }));
                  setError(null);
                }}
              />
            </Field.Root>
            {workspaceName ? (
              <Text fontSize="xs" color="ui.text.tertiary">
                In {workspaceName}
              </Text>
            ) : null}
            {error ? (
              <Text role="alert" fontSize="sm" color="status.danger.fg">
                {error}
              </Text>
            ) : null}
            <Button
              type="submit"
              alignSelf="flex-end"
              size="sm"
              loading={busy}
              loadingText="Creating…"
              disabled={!hydrated || busy || !identity.name.trim()}
            >
              Continue <ArrowRight size={15} />
            </Button>
          </VStack>
        </Box>
      ) : (
        <VStack align="stretch" gap="6">
          <Box>
            <Heading
              ref={headingRef}
              tabIndex={-1}
              outline="none"
              as="h1"
              fontSize="2xl"
              fontWeight="650"
              letterSpacing="-0.02em"
              color="ui.text.primary"
            >
              Connect your tools
            </Heading>
            <Text mt="2" fontSize="sm" color="ui.text.secondary">
              Bring tools and data into {currentProject.name}. You can add more anytime.
            </Text>
          </Box>
          {renderModules(currentProject, setConfiguredCount)}
          <Box
            position="sticky"
            bottom="0"
            bg="ui.surface.drawer"
            borderTopWidth="1px"
            borderColor="ui.border.subtle"
            py="4"
            zIndex="1"
          >
            {error ? (
              <Text role="alert" fontSize="sm" mb="3" color="status.danger.fg">
                {error}
              </Text>
            ) : null}
            <Flex justify="space-between" align="center" gap="3">
              <Text fontSize="xs" color="ui.text.tertiary">
                All modules are optional.
              </Text>
              <Button
                size="sm"
                onClick={() => void openWorkspace()}
                loading={busy}
                loadingText="Opening…"
                disabled={busy}
              >
                Open workspace <ArrowRight size={15} />
              </Button>
            </Flex>
          </Box>
        </VStack>
      )}
    </Box>
  );
}

export function WorkspaceSetupWizard(props: WorkspaceSetupWizardProps) {
  const { user } = useAuth();
  return (
    <WorkspaceSetupWizardView
      {...props}
      identityDraftKey={
        user ? setupDraftKey(user.uid, props.workspaceId || 'personal', 'identity') : undefined
      }
      renderModules={(project, onConfiguredCountChange) => (
        <IntegrationsSettingsPanel
          hasProject
          canManage={project.current_user_role === 'owner' || project.current_user_role === 'admin'}
          workspaceExportsEnabled={isGoogleWorkspaceExportDeliveryEnabledClient()}
          showHeader={false}
          showGrowthProgramSetup={false}
          onboarding
          activeModuleId={props.activeModuleId}
          onActiveModuleChange={props.onActiveModuleChange}
          returnTo={props.returnTo}
          onConfiguredCountChange={onConfiguredCountChange}
        />
      )}
    />
  );
}
